import { cn } from "@productlane/lib/src/cn"
import { CopyButton, Tooltip } from "@productlane/ui"

export interface DnsEntry {
	type: "A" | "CNAME" | "TXT" | "MX"
	host: string
	value: string
	/** Cloudflare's own column. Other providers have no such setting. */
	proxy?: string
}

interface Props {
	entries: DnsEntry[]
}

const TYPE_COLUMN = "w-20 flex-none"
const HOST_COLUMN = "w-1/4 min-w-0 flex-none"
const VALUE_COLUMN = "min-w-0 flex-1"
const PROXY_COLUMN = "w-24 flex-none"

export function DnsEntryTable({ entries }: Props) {
	const showProxy = entries.some((e) => !!e.proxy)

	return (
		<div className="flex flex-col">
			<div className="flex gap-3 border-b border-gray-200 pb-2 text-xxs font-medium uppercase tracking-wide text-tertiary dark:border-gray-700/80">
				<div className={TYPE_COLUMN}>Type</div>
				<div className={HOST_COLUMN}>Host</div>
				<div className={VALUE_COLUMN}>Value</div>
				{showProxy ? <div className={PROXY_COLUMN}>Proxy</div> : null}
			</div>

			<div className="divide-y divide-gray-200 dark:divide-gray-700/80">
				{entries.map((e) => (
					<DnsEntryTableRow
						key={`${e.type}/${e.host}/${e.value}`}
						entry={e}
						showProxy={showProxy}
					/>
				))}
			</div>
		</div>
	)
}

export function DnsEntryTableRow({
	entry,
	showProxy,
}: {
	entry: DnsEntry
	showProxy?: boolean
}) {
	return (
		<div className="flex items-center gap-3 py-2.5">
			<div
				className={cn(
					TYPE_COLUMN,
					"font-mono text-xs uppercase text-secondary",
				)}
			>
				{entry.type}
			</div>
			<DnsEntryTableCell value={entry.host} className={HOST_COLUMN} copyable />
			<DnsEntryTableCell
				value={entry.value}
				className={VALUE_COLUMN}
				copyable
			/>
			{showProxy ? (
				<div className={cn(PROXY_COLUMN, "text-[13px] text-secondary")}>
					{entry.proxy ? (
						<Tooltip
							label="Cloudflare only. Proxying blocks the certificate check."
							shouldWrapChildren
						>
							<span>{entry.proxy}</span>
						</Tooltip>
					) : null}
				</div>
			) : null}
		</div>
	)
}

export function DnsEntryTableCell({
	value,
	copyable,
	wrapable,
	copyTooltip,
	className,
}: {
	value: string
	copyTooltip?: string
	copyable?: boolean
	wrapable?: boolean
	className?: string
}) {
	return (
		<div className={cn("flex items-center gap-1", className)} title={value}>
			<div
				className={cn(
					"font-mono text-[13px] text-primary",
					wrapable ? "break-all" : "min-w-0 truncate",
				)}
			>
				{value}
			</div>
			{copyable && (
				<CopyButton
					value={value}
					tooltip={copyTooltip}
					className="!text-tertiary hover:!text-primary"
				/>
			)}
		</div>
	)
}
